import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { globalSearch, customerSearch } from '../services/dashboardApi';
import CustomerHistoryModal from './CustomerHistoryModal';
import { inputStyle } from './FormField';
import useGlobalShortcut from '../utils/useGlobalShortcut';
import { getNextTabIndex } from '../utils/rovingTabIndex';

const TYPE_LABELS = {
  design: 'Design', baseDesign: 'Base Design', product: 'Product', category: 'Category',
  size: 'Size', customer: 'Customer', refParty: 'Ref. Party', group: 'Group',
  location: 'Location', user: 'User', recommendation: 'Recommendation',
  anomaly: 'Anomaly', demandSignal: 'Demand Signal', customerX: 'Customer',
};

export default function GlobalSearchBar({ customersOnly = false }) {
  const [activeCustomer, setActiveCustomer] = useState(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return undefined;
    }
    const timeout = setTimeout(async () => {
      try {
        const [data, cust] = await Promise.all([
          customersOnly ? Promise.resolve({ items: [] }) : globalSearch(query).catch(() => ({ items: [] })),
          customerSearch(query).catch(() => []),
        ]);
        const customers = cust.map((c) => ({
          type: 'customerX', id: c.id, name: c.name, city: c.city, state: c.state,
        }));
        setResults([...customers, ...data.items.filter((i) => i.type !== 'customer')]);
        setOpen(true);
        setHighlightedIndex(-1);
      } catch {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(timeout);
  }, [query, customersOnly]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Global "/" focuses search from anywhere in the app — a common, well-
  // known convention (Slack, GitHub) — except while already typing elsewhere.
  useGlobalShortcut('/', useCallback(() => {
    inputRef.current?.focus();
  }, []));

  const handleSelect = (result) => {
    setOpen(false);
    setQuery('');
    setHighlightedIndex(-1);
    if (result.type === 'customerX') { setActiveCustomer({ id: result.id, name: result.name }); return; }
    if (result.link) navigate(result.link);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setHighlightedIndex(-1);
      return;
    }
    if (!open || results.length === 0) return;

    if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelect(results[highlightedIndex]);
      return;
    }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
      e.preventDefault();
      // Reuses the same verified index-transition logic the TabList
      // component uses — one tested implementation, two consistent
      // keyboard-navigable widgets (tablist and this results listbox).
      const base = highlightedIndex === -1 ? 0 : highlightedIndex;
      const next = e.key === 'ArrowDown' && highlightedIndex === -1
        ? 0
        : getNextTabIndex(base, e.key, results.length);
      setHighlightedIndex(next);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', maxWidth: 360, width: '100%' }}>
      <input
        ref={inputRef}
        type="search"
        placeholder={customersOnly ? 'Search customer, city, state, country…' : 'Search everything… (press / to focus)'}
        aria-label="Global search"
        role="combobox"
        aria-expanded={open}
        aria-controls="global-search-listbox"
        aria-activedescendant={highlightedIndex >= 0 ? `search-result-${highlightedIndex}` : undefined}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        onKeyDown={handleKeyDown}
        style={inputStyle}
      />
      {open && (
        <ul
          id="global-search-listbox"
          role="listbox"
          aria-label="Search results"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'var(--shadow-card)',
            listStyle: 'none',
            padding: 4,
            maxHeight: 340,
            overflowY: 'auto',
            zIndex: 20,
          }}
        >
          {results.length === 0 && (
            <li style={{ padding: '8px 12px', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
              No results
            </li>
          )}
          {results.map((r, i) => (
            <li key={`${r.type}-${r.id}`} id={`search-result-${i}`} role="option" aria-selected={highlightedIndex === i}>
              <button
                type="button"
                onClick={() => handleSelect(r)}
                onMouseEnter={() => setHighlightedIndex(i)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  fontSize: 'var(--font-size-sm)',
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  background: highlightedIndex === i ? 'var(--color-surface-alt)' : 'transparent',
                  cursor: r.link ? 'pointer' : 'default',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  fontFamily: 'inherit',
                  color: 'var(--color-text-primary)',
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}{r.type === 'customerX' && r.city ? ` — ${r.city}` : ''}</span>
                <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)', flexShrink: 0 }}>
                  {r.type === 'customerX' ? (r.state || 'Customer') : (TYPE_LABELS[r.type] || r.type)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {activeCustomer && <CustomerHistoryModal customer={activeCustomer} onClose={() => setActiveCustomer(null)} />}
    </div>
  );
}
